import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    TextField,
    MenuItem,
    Grid,
    InputAdornment,
    Paper,
    Divider,
    Stack,
    Fab,
    Zoom,
    useScrollTrigger,
    CircularProgress
} from '@mui/material';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer
} from 'recharts';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import LocationCityIcon from '@mui/icons-material/LocationOn';
import ExploreIcon from '@mui/icons-material/Explore';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { businessService } from '../services/businessService';
import { categoryService } from '../services/categoryService';
import { cityService } from '../services/cityService';
import { Category } from '../types/category';
import { City } from '../types/city';
import { Business } from '../types/business';
import BusinessCard from '../components/common/BusinessCard';
import useSEO from '../hooks/useSEO';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { featuredService } from '../services/featuredService';
import { categoryPath, cityPath } from '../utils/browsePaths';

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 100,
    });

    const [categories, setCategories] = useState<Category[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [recentBusinesses, setRecentBusinesses] = useState<Business[]>([]);
    // Premium listings' homepage spots (limited, rotating).
    const [premiumBusinesses, setPremiumBusinesses] = useState<Business[]>([]);
    useEffect(() => {
        featuredService.premiumListings().then(list => setPremiumBusinesses(list as Business[])).catch(() => setPremiumBusinesses([]));
    }, []);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [visibleCategories, setVisibleCategories] = useState(8);
    const [visibleCities, setVisibleCities] = useState(8);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const getCityImage = (cityName: string) => {
        const images: Record<string, string> = {
            'Sydney': '/Sydney-min.jpg',
            'Melbourne': '/Melbourne-min.jpg',
            'Brisbane': '/Brisbane-min.jpg',
            'Gold Coast': '/Gold-Coast-min.jpg',
            'Perth': '/Perth-min.jpg',
            'Adelaide': '/Adelaide-min.jpg',
            'Canberra': '/Canberra-min.jpg',
            'Darwin': '/Darwin-min.jpg',
            'Tasmania': '/Tasmania-min.jpg',
            'Iran': 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/22/State_flag_of_Iran_%281964%E2%80%931980%29.svg/800px-State_flag_of_Iran_%281964%E2%80%931980%29.svg.png'
        };
        return images[cityName] || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=300&h=300&q=80';
    };

    const getCategoryStyle = (cat: Category) => {
        const name = cat.name;
        const slug = cat.slug || '';

        const styles: Record<string, { bg: string, iconBg: string, color: string, icon: React.ReactNode, en: string }> = {
            'barber': { bg: '#f3f0ff', iconBg: '#dcd3ff', color: '#5f3dc4', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>💇‍♂️</Box>, en: 'Barber shop' },
            'travel': { bg: '#e7f5ff', iconBg: '#c5e4ff', color: '#1971c2', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>✈️</Box>, en: 'Travel agency' },
            'real-estate': { bg: '#fff9db', iconBg: '#fff3bf', color: '#e67700', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🏠</Box>, en: 'Real estate' },
            'immigration': { bg: '#e6fcf5', iconBg: '#c3fae8', color: '#099268', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🌐</Box>, en: 'Immigration' },
            'driving': { bg: '#fff5f5', iconBg: '#ffe3e3', color: '#c92a2a', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🚗</Box>, en: 'Driving school' },
            'iran': { 
                bg: '#f4fce3', 
                iconBg: '#ebfbee', 
                color: '#5c940d', 
                icon: <Box component="img" src="/iran-flag.png" alt="" loading="lazy" sx={{ width: 55, height: 40, objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />, 
                en: 'Business in Iran' 
            },
            'doctor': { bg: '#fff0f6', iconBg: '#ffdeeb', color: '#c2255c', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🩺</Box>, en: 'Specialist doctor' },
            'dentist': { bg: '#fff0f6', iconBg: '#ffdeeb', color: '#c2255c', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🦷</Box>, en: 'Dentist' },
            'accountant': { bg: '#f8f9fa', iconBg: '#e9ecef', color: '#495057', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>💰</Box>, en: 'Accountant' },
            'home': { bg: '#f3f0ff', iconBg: '#dcd3ff', color: '#5f3dc4', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🏗️</Box>, en: 'Home services' },
            'transport': { bg: '#e7f5ff', iconBg: '#c5e4ff', color: '#1971c2', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🚛</Box>, en: 'Transportation' },
            'exchange': { bg: '#f8f9fa', iconBg: '#e9ecef', color: '#495057', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>💱</Box>, en: 'Exchange' },
            'flowers': { bg: '#fff0f6', iconBg: '#ffdeeb', color: '#c2255c', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🌸</Box>, en: 'Flowers' },
            'legal': { bg: '#e7f5ff', iconBg: '#c5e4ff', color: '#1971c2', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>⚖️</Box>, en: 'Legal services' },
            'restaurant': { bg: '#fff9db', iconBg: '#fff3bf', color: '#e67700', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🍽️</Box>, en: 'Restaurant' },
            'fastfood': { bg: '#fff5f5', iconBg: '#ffe3e3', color: '#c92a2a', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🍔</Box>, en: 'Fast food' },
            'beauty': { bg: '#fff0f6', iconBg: '#ffdeeb', color: '#c2255c', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>💅</Box>, en: 'Beauty salon' },
            'cleaning': { bg: '#e6fcf5', iconBg: '#c3fae8', color: '#099268', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🧹</Box>, en: 'Cleaning' },
            'photography': { bg: '#f3f0ff', iconBg: '#dcd3ff', color: '#5f3dc4', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>📷</Box>, en: 'Photography' },
            'wholesale': { bg: '#f4fce3', iconBg: '#ebfbee', color: '#5c940d', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>📦</Box>, en: 'Wholesale' },
            'printing': { bg: '#f8f9fa', iconBg: '#e9ecef', color: '#495057', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🖨️</Box>, en: 'Printing' },
            'mechanic': { bg: '#e7f5ff', iconBg: '#c5e4ff', color: '#1971c2', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🔧</Box>, en: 'Auto Mechanic' },
            'bakery': { bg: '#fff9db', iconBg: '#fff3bf', color: '#e67700', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🥖</Box>, en: 'Bakery' },
            'education': { bg: '#f3f0ff', iconBg: '#dcd3ff', color: '#5f3dc4', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🎓</Box>, en: 'Education' },
            'sports': { bg: '#e6fcf5', iconBg: '#c3fae8', color: '#099268', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🏃‍♂️</Box>, en: 'Sports & Health' },
            'liquor': { bg: '#fff5f5', iconBg: '#ffe3e3', color: '#c92a2a', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🍷</Box>, en: 'Liquor shop' },
            'wedding': { bg: '#fff0f6', iconBg: '#ffdeeb', color: '#c2255c', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>💍</Box>, en: 'Weddings & events' },
            'instrument-repair': { bg: '#fff4e6', iconBg: '#ffe8cc', color: '#d9480f', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🎻</Box>, en: 'Instrument repairs' },
            'finance': { bg: '#e7f5ff', iconBg: '#c5e4ff', color: '#1971c2', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🏦</Box>, en: 'Loans & insurance' },
            'computer': { bg: '#f3f0ff', iconBg: '#dcd3ff', color: '#5f3dc4', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>💻</Box>, en: 'Computers & web design' },
            'tailor': { bg: '#fff0f6', iconBg: '#ffdeeb', color: '#c2255c', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🧵</Box>, en: 'Tailor' },
            'psychologist': { bg: '#e6fcf5', iconBg: '#c3fae8', color: '#099268', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🧠</Box>, en: 'Psychologist' },
            'craft': { bg: '#fff4e6', iconBg: '#ffe8cc', color: '#d9480f', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🏺</Box>, en: 'Handicrafts & gallery' },
            'carpet': { bg: '#fff5f5', iconBg: '#ffe3e3', color: '#c92a2a', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🧶</Box>, en: 'Persian rugs' },
            'supermarket': { bg: '#f4fce3', iconBg: '#ebfbee', color: '#5c940d', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🛒</Box>, en: 'Supermarket' },
            'pastry': { bg: '#fff0f6', iconBg: '#ffdeeb', color: '#c2255c', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🎂</Box>, en: 'Pastry & cakes' },
            'translator': { bg: '#e7f5ff', iconBg: '#c5e4ff', color: '#1971c2', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>📝</Box>, en: 'Translator' },
            'dj': { bg: '#f3f0ff', iconBg: '#dcd3ff', color: '#5f3dc4', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🎧</Box>, en: 'Music, DJ & media' },
            'gp': { bg: '#fff0f6', iconBg: '#ffdeeb', color: '#c2255c', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🩺</Box>, en: 'General practitioner' },
            'coffee': { bg: '#fff9db', iconBg: '#fff3bf', color: '#e67700', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>☕</Box>, en: 'Coffee shop' },
            'physio': { bg: '#e6fcf5', iconBg: '#c3fae8', color: '#099268', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>💆</Box>, en: 'Physio & chiropractor' },
            'vet': { bg: '#f4fce3', iconBg: '#ebfbee', color: '#5c940d', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🐾</Box>, en: 'Vet clinic' },
            'catering': { bg: '#fff9db', iconBg: '#fff3bf', color: '#e67700', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🍲</Box>, en: 'Catering' },
            'music': { bg: '#fff4e6', iconBg: '#ffe8cc', color: '#d9480f', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>🎻</Box>, en: 'Musical instruments' },
            'misc': { bg: '#f8f9fa', iconBg: '#e9ecef', color: '#495057', icon: <Box component="span" sx={{ fontSize: '1.8rem' }}>📁</Box>, en: 'Miscellaneous' },
        };

        const getCleanEn = (s: string) => {
            try {
                const decoded = decodeURIComponent(s);
                return decoded.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            } catch {
                return s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            }
        };

        const defaultStyle = { bg: '#f8f9fa', iconBg: '#e9ecef', color: '#495057', icon: <BusinessIcon />, en: getCleanEn(slug) };

        // Every current category by its slug. The keyword rules below are only a fallback
        // for categories added later (and they guess: "ایرانی" vs "ایران", "تعمیر" ...).
        const bySlug: Record<string, string> = {
            'iranian-hair-dresser-barber-shop': 'barber',
            'persian-driving-school': 'driving',
            'flight-center': 'travel',
            'real-estate-agent': 'real-estate',
            'iran-australia-business': 'iran',
            'persian-wedding-ceremony': 'wedding',
            'persian-musical-repairs': 'instrument-repair',
            'persian-accountant-australia': 'accountant',
            'moving': 'transport',
            'house-construction-refurbishment': 'home',
            'visa-immigration': 'immigration',
            'persian-broker': 'finance',
            'persian-computer-technician-australia': 'computer',
            'tailor': 'tailor',
            'law': 'legal',
            'dentist': 'dentist',
            'persian-restaurant-chef': 'restaurant',
            'persian-psychologist': 'psychologist',
            'beauty-salon': 'beauty',
            'floral-design': 'flowers',
            'cleaning': 'cleaning',
            'business-card-printing': 'printing',
            'exchange': 'exchange',
            'craft-art-gallery': 'craft',
            'iranian-wholesaler': 'wholesale',
            'photography': 'photography',
            'persian-carpet-rugs-kilim': 'carpet',
            'persian-supermarket': 'supermarket',
            'fastfood': 'fastfood',
            'pastery': 'pastry',
            'persian-translator': 'translator',
            'cheesta-australia-miscellaneous': 'misc',
            'persian-iranian-schools-institutes': 'education',
            'alcohol-drinks': 'liquor',
            'persian-dj-australia': 'dj',
            'mechanic': 'mechanic',
            'persian-bakery': 'bakery',
            'sports-health': 'sports',
            'gp': 'gp',
            'persian-specialist': 'doctor',
            'persian-coffeshop': 'coffee',
            'persian-physiotherapist-chiropractor': 'physio',
            'iranian-vet': 'vet',
            'persian-catering': 'catering',
        };
        const exact = bySlug[slug.toLowerCase()];
        if (exact && styles[exact]) return styles[exact];

        // Match by Persian Name
        // The word "ساز" (instrument) on its own - not inside بدنسازی, سازمان, etc.
        if (/(^|\s)ساز(ها|های)?(\s|$)/.test(name) || name.includes('موسیقی')) return styles['music'];
        if (name.includes('آرایشگاه')) return styles['barber'];
        if (name.includes('آژانس') || name.includes('هواپیمایی')) return styles['travel'];
        if (name.includes('املاک')) return styles['real-estate'];
        if (name.includes('مهاجرت')) return styles['immigration'];
        if (name.includes('رانندگی')) return styles['driving'];
        // "ایران" on its own only: "ایرانی" (Iranian) is in many ordinary category names.
        if (/ایران(?!ی)/.test(name) && !name.includes('مدرسه') && !name.includes('آموزش')) return styles['iran'];
        if (name.includes('دندان')) return styles['dentist'];
        if (name.includes('پزشک')) return styles['doctor'];
        if (name.includes('حسابدار')) return styles['accountant'];
        if (name.includes('ساختمان') || name.includes('خانه')) return styles['home'];
        if (name.includes('حمل') || name.includes('اتوبوس') || name.includes('باربری') || name.includes('ترانزیت')) return styles['transport'];
        if (name.includes('صرافی') || name.includes('ارز')) return styles['exchange'];
        if (name.includes('گل')) return styles['flowers'];
        if (name.includes('وکالت') || name.includes('حقوقی') || name.includes('وکیل')) return styles['legal'];
        if (name.includes('رستوران') || name.includes('کترینگ')) return styles['restaurant'];
        if (name.includes('فست فود')) return styles['fastfood'];
        if (name.includes('زیبایی') || name.includes('ناخن') || name.includes('آرایش')) return styles['beauty'];
        if (name.includes('نظافت') || name.includes('پاکیزگی')) return styles['cleaning'];
        if (name.includes('عکاسی') || name.includes('فیلم') || name.includes('آتلیه')) return styles['photography'];
        if (name.includes('عمده')) return styles['wholesale'];
        if (name.includes('چاپ') || name.includes('تبلیغات') || name.includes('صحافی')) return styles['printing'];
        if (name.includes('مکانیک') || name.includes('خودرو') || name.includes('تعمیر')) return styles['mechanic'];
        if (name.includes('نانوایی') || name.includes('نان')) return styles['bakery'];
        if (name.includes('مدرسه') || name.includes('آموزش') || name.includes('کلاس')) return styles['education'];
        if (name.includes('ورزش') || name.includes('سلامت') || name.includes('بدنسازی')) return styles['sports'];
        if (name.includes('مشروبات') || name.includes('شراب')) return styles['liquor'];
        if (name.includes('متفرقه')) return styles['misc'];

        // Match by Slug (Fallback)
        if (slug.includes('barber')) return styles['barber'];
        if (slug.includes('travel') || slug.includes('flight')) return styles['travel'];
        if (slug.includes('real') || slug.includes('estate')) return styles['real-estate'];
        if (slug.includes('immig')) return styles['immigration'];
        if (slug.includes('drive') || slug.includes('driving')) return styles['driving'];
        if (slug.includes('music')) return styles['music'];
        if (/(^|-)iran($|-)/.test(slug)) return styles['iran'];
        if (slug.includes('dentist')) return styles['dentist'];
        if (slug.includes('doctor') || slug.includes('medical')) return styles['doctor'];
        if (slug.includes('account')) return styles['accountant'];
        if (slug.includes('home') || slug.includes('service') || slug.includes('building')) return styles['home'];
        if (slug.includes('transport') || slug.includes('move')) return styles['transport'];
        if (slug.includes('exchange') || slug.includes('sarafi')) return styles['exchange'];
        if (slug.includes('flower')) return styles['flowers'];
        if (slug.includes('legal') || slug.includes('lawyer')) return styles['legal'];
        if (slug.includes('restaur')) return styles['restaurant'];
        if (slug.includes('fastfood')) return styles['fastfood'];
        if (slug.includes('beauty') || slug.includes('salon')) return styles['beauty'];
        if (slug.includes('clean')) return styles['cleaning'];
        if (slug.includes('photograph')) return styles['photography'];
        if (slug.includes('wholesale')) return styles['wholesale'];
        if (slug.includes('print')) return styles['printing'];
        if (slug.includes('mechanic')) return styles['mechanic'];
        if (slug.includes('bakery')) return styles['bakery'];
        if (slug.includes('school') || slug.includes('edu')) return styles['education'];
        if (slug.includes('sport') || slug.includes('health')) return styles['sports'];
        if (slug.includes('liquor') || slug.includes('wine') || slug.includes('alkol')) return styles['liquor'];

        return defaultStyle;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [cats, cits, latest] = await Promise.all([
                    categoryService.getAll(),
                    cityService.getAll(),
                    businessService.getAll(undefined, undefined, undefined, 4)
                ]);

                const cityOrder = ['Sydney', 'Melbourne', 'Brisbane', 'Gold Coast', 'Adelaide', 'Perth', 'Canberra', 'Tasmania', 'Darwin', 'Iran'];
                const sortedCities = [...cits].sort((a, b) => {
                    const indexA = cityOrder.indexOf(a.name);
                    const indexB = cityOrder.indexOf(b.name);
                    const posA = indexA !== -1 ? indexA : 999;
                    const posB = indexB !== -1 ? indexB : 999;
                    return posA - posB;
                });

                setCategories(cats);
                setCities(sortedCities);
                setRecentBusinesses(latest.slice(0, 4));
            } catch (error) {
                console.error('Failed to fetch data', error);
            }
        };
        fetchData();
    }, []);

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (searchQuery) params.append('q', searchQuery);
        if (selectedCategory) params.append('categoryId', selectedCategory);
        if (selectedCity) params.append('cityId', selectedCity);
        navigate(`/search?${params.toString()}`);
    };

    useSEO({
        title: 'چیستا | جامع‌ترین مرجع معرفی کسب‌وکارهای ایرانیان استرالیا',
        description: 'چیستا جامع‌ترین دایرکتوری کسب‌وکارهای ایرانیان استرالیاست. رستوران، پزشک، وکیل، مشاور و صدها کسب‌وکار ایرانی نزدیک خود را پیدا کنید یا کسب‌وکار خود را رایگان ثبت کنید.',
        canonical: '/',
        jsonLd: {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'Organization',
                    '@id': 'https://cheesta.com.au/#organization',
                    name: 'Cheesta Australia',
                    alternateName: 'چیستا',
                    url: 'https://cheesta.com.au/',
                    logo: 'https://cheesta.com.au/logo512.png?v=2',
                    email: 'support@cheesta.com.au',
                },
                {
                    '@type': 'WebSite',
                    '@id': 'https://cheesta.com.au/#website',
                    url: 'https://cheesta.com.au/',
                    name: 'چیستا',
                    alternateName: 'Cheesta - Persian Business Directory Australia',
                    inLanguage: 'fa',
                    publisher: { '@id': 'https://cheesta.com.au/#organization' },
                    potentialAction: {
                        '@type': 'SearchAction',
                        target: { '@type': 'EntryPoint', urlTemplate: 'https://cheesta.com.au/search?q={search_term_string}' },
                        'query-input': 'required name=search_term_string',
                    },
                },
            ],
        },
    });

    return (
        <Box sx={{ bgcolor: '#f8fafc', position: 'relative' }}>
            <Zoom in={trigger}>
                <Box
                    onClick={scrollToTop}
                    role="presentation"
                    sx={{ position: 'fixed', bottom: { xs: 16, md: 32 }, right: { xs: 16, md: 32 }, zIndex: 1000 }}
                >
                    <Fab
                        size="medium"
                        aria-label="scroll back to top"
                        sx={{
                            bgcolor: '#2854a1',
                            color: 'white',
                            '&:hover': { bgcolor: '#1e3d7a' },
                            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                        }}
                    >
                        <KeyboardArrowUpIcon />
                    </Fab>
                </Box>
            </Zoom>

            <Box
                sx={{
                    position: 'relative',
                    minHeight: { xs: '70vh', md: '75vh' },
                    backgroundImage: 'url(/Cheesta-Australia.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center 30%',
                    display: 'flex',
                    alignItems: 'center',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.4))',
                        zIndex: 1
                    }
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            mb: 2,
                            color: 'white',
                            fontSize: { xs: '1.8rem', md: '2.5rem' },
                            textShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}
                    >
                        چیستا
                    </Typography>
                    <Typography
                        variant="h6"
                        component="p"
                        sx={{
                            mb: 6,
                            color: 'rgba(255,255,255,0.9)',
                            fontWeight: 400,
                            maxWidth: '700px',
                            mx: 'auto',
                            direction: 'rtl',
                            lineHeight: 1.6,
                            fontSize: { xs: '0.9rem', md: '1rem' }
                        }}
                    >
                        جامع ترین مرجع معرفی کسب و کارهای ایرانیان استرالیا
                    </Typography>

                    <Paper
                        elevation={24}
                        sx={{
                            p: { xs: 2, md: '4px' },
                            borderRadius: { xs: 3, md: 10 },
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            maxWidth: '850px',
                            mx: 'auto',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            gap: { xs: 1.25, md: 0 },
                            border: '1px solid rgba(255,255,255,0.3)',
                            transform: { xs: 'none', md: 'scale(0.95)' }
                        }}
                    >
                        <TextField
                            placeholder="دنبال چه هستید؟"
                            fullWidth
                            variant="standard"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ ml: 2 }}>
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                sx: { px: 2, py: 1, fontSize: '0.95rem' }
                            }}
                            sx={{ flexGrow: 2, border: { xs: '1px solid #e2e8f0', md: 'none' }, borderRadius: { xs: 1.5, md: 0 }, bgcolor: { xs: '#fff', md: 'transparent' } }}
                        />
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, my: 1.5, mx: 1 }} />
                        <TextField
                            select
                            fullWidth
                            variant="standard"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            InputProps={{
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ ml: 1 }}>
                                        <BusinessIcon color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { px: 2, py: 1, fontSize: '0.95rem', minWidth: { md: '160px' } }
                            }}
                            sx={{ flexGrow: 1, display: { xs: 'none', md: 'inline-flex' } }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.85rem' }}>تمام دسته‌ها</MenuItem>
                            {categories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.85rem' }}>{cat.name}</MenuItem>
                            ))}
                        </TextField>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, my: 1.5, mx: 1 }} />
                        <TextField
                            select
                            fullWidth
                            variant="standard"
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                            InputProps={{
                                disableUnderline: true,
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ ml: 1 }}>
                                        <LocationCityIcon color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { px: 2, py: 1, fontSize: '0.95rem', minWidth: { md: '140px' } }
                            }}
                            sx={{ flexGrow: 1, display: { xs: 'none', md: 'inline-flex' } }}
                        >
                            <MenuItem value="" sx={{ fontSize: '0.85rem' }}>تمام استرالیا</MenuItem>
                            {cities.map((city) => (
                                <MenuItem key={city.id} value={city.id} sx={{ fontSize: '0.85rem' }}>{city.name}</MenuItem>
                            ))}
                        </TextField>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleSearch}
                            sx={{
                                minWidth: '120px',
                                width: { xs: '100%', md: 'auto' },
                                height: { xs: '48px', md: '44px' },
                                borderRadius: { xs: 1.5, md: 22 },
                                ml: { md: 1 },
                                bgcolor: '#2854a1',
                                fontWeight: 'bold',
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                '&:hover': { bgcolor: '#1e3d7a' }
                            }}
                        >
                            جستجو
                        </Button>
                    </Paper>
                </Container>
            </Box>

            {/* Premium listings: homepage spots */}
            {premiumBusinesses.length > 0 && (
                <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 8 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, direction: 'rtl' }}>
                        <WorkspacePremiumIcon sx={{ color: '#6b21a8' }} />
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>کسب و کارهای برگزیده</Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {premiumBusinesses.map((biz) => (
                            <Grid key={biz.id} size={{ xs: 12, sm: 6, md: 3 }}>
                                <BusinessCard business={biz} />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            )}

            {/* Feature: featured (boosted) listings */}
            <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 7 } }}>
                <Box
                    dir="rtl"
                    sx={{
                        display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' },
                        justifyContent: 'space-between', gap: 2.5, p: { xs: 3, md: 4 }, borderRadius: 4,
                        background: 'linear-gradient(135deg, #fffaf0 0%, #fdf0d5 100%)', border: '1px solid #f6e0b5', textAlign: 'right',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <Box sx={{ flexShrink: 0, width: 52, height: 52, borderRadius: '50%', bgcolor: '#b7791f', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <RocketLaunchIcon />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.1rem', md: '1.3rem' }, color: '#1e293b' }}>
                                کسب و کار خود را ویژه کنید
                            </Typography>
                            <Typography sx={{ color: '#4a5568', mt: 0.5, lineHeight: 1.8 }}>
                                ثبت آگهی رایگان است. آگهی «ویژه» در بالای نتایج جستجو نمایش داده می‌شود و آگهی «برگزیده» در صفحه اصلی هم جای ویژه دارد — با پرداخت سالانه، ۲ ماه رایگان.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
                        <Button component={RouterLink} to="/pricing" variant="contained"
                            sx={{ bgcolor: '#b7791f', borderRadius: 2, px: 3, fontWeight: 700, '&:hover': { bgcolor: '#975a16' } }}>
                            مشاهده تعرفه‌ها
                        </Button>
                        <Button component={RouterLink} to="/cheesta-help#featured" variant="outlined"
                            sx={{ borderRadius: 2, color: '#975a16', borderColor: '#b7791f' }}>
                            بیشتر بدانید
                        </Button>
                    </Box>
                </Box>
            </Container>

            {recentBusinesses.length > 0 && (
                <Container maxWidth="lg" sx={{ py: 10 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 4, direction: 'rtl' }}>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <FiberNewIcon sx={{ color: '#2854a1', ml: 1 }} fontSize="large" />
                                <Typography variant="h4" component="h2" sx={{ fontWeight: 800, color: '#1a202c' }}>
                                    جدیدترین بیزینس ها
                                </Typography>
                            </Box>
                            <Typography variant="body1" color="text.secondary">
                                آخرین کسب و کارهای ثبت شده
                            </Typography>
                        </Box>
                        <Button
                            endIcon={<ArrowBackIosNewIcon sx={{ fontSize: 12, mr: 1, transform: 'rotate(180deg)' }} />}
                            component={RouterLink}
                            to="/search"
                            sx={{ color: '#2854a1', fontWeight: 'bold', whiteSpace: 'nowrap', flexShrink: 0, textDecoration: 'none !important' }}
                        >
                            مشاهده همه
                        </Button>
                    </Box>
                    <Grid container spacing={3}>
                        {recentBusinesses.map((biz) => (
                            <Grid key={biz.id} size={{ xs: 12, sm: 6, md: 3 }}>
                                <BusinessCard business={biz} />
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            )}

            {/* Categories Section - Icon Based Tiles */}
            <Box sx={{ py: 10, bgcolor: 'white' }}>
                <Container sx={{ maxWidth: '1100px !important' }}>
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 2, color: '#1a202c' }}>
                            در جستجوی چه هستید؟
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#718096', maxWidth: '600px', mx: 'auto', direction: 'rtl' }}>
                            کسب و کارهای ثبت شده بر اساس دسته‌بندی و شهر را در اینجا بیابید
                        </Typography>
                    </Box>

                    <Grid container spacing={2}>
                        {categories.slice(0, visibleCategories).map((cat) => {
                            const style = getCategoryStyle(cat);
                            return (
                                <Grid key={cat.id} size={{ xs: 6, sm: 4, md: 3 }}>
                                    <Paper
                                        component={RouterLink}
                                        to={categoryPath(cat)}
                                        elevation={0}
                                        sx={{
                                            textDecoration: 'none !important',
                                            color: 'inherit',
                                            height: 200,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            bgcolor: style.bg,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                boxShadow: '0 12px 30px rgba(0,0,0,0.08)'
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: 3,
                                                bgcolor: style.iconBg,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: style.color,
                                                mb: 2.5
                                            }}
                                        >
                                            {style.icon}
                                        </Box>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 800,
                                                color: '#2d3748',
                                                mb: 0.5,
                                                fontSize: '0.95rem'
                                            }}
                                        >
                                            {cat.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontWeight: 600,
                                                color: style.color,
                                                opacity: 0.8,
                                                fontSize: '0.8rem',
                                                letterSpacing: '0.2px'
                                            }}
                                        >
                                            {style.en}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {categories.length > visibleCategories && (
                        <Box sx={{ textAlign: 'center', mt: 6 }}>
                            <Button
                                variant="outlined"
                                onClick={() => setVisibleCategories(prev => prev + 8)}
                                sx={{
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1,
                                    color: '#2854a1',
                                    borderColor: '#2854a1',
                                    fontWeight: 700,
                                    '&:hover': {
                                        borderColor: '#1a3a7a',
                                        bgcolor: 'rgba(40, 84, 161, 0.05)'
                                    }
                                }}
                            >
                                مشاهده بیشتر
                            </Button>
                        </Box>
                    )}
                </Container>
            </Box>
            {/* City Statistics Section */}
            <Box sx={{ py: 10, bgcolor: '#f5f5f3' }}>
                <Container maxWidth="lg">
                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <Typography
                            variant="h5"
                            component="h2"
                            sx={{
                                fontWeight: 500,
                                mb: 1,
                                color: '#1a1a1a',
                                fontSize: '24px',
                                direction: 'rtl'
                            }}
                        >
                            کسب‌وکارها به تفکیک شهر
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#777',
                                fontSize: '14px',
                                direction: 'rtl'
                            }}
                        >
                            تعداد آگهی‌های ثبت شده در شهرهای مختلف استرالیا
                        </Typography>
                    </Box>

                    {/* Summary Stats */}
                    <Box sx={{ display: 'flex', gap: '10px', mb: 5, flexWrap: 'wrap', direction: 'rtl' }}>
                        <Paper elevation={0} sx={{ p: '12px 20px', flex: 1, minWidth: '120px', border: '0.5px solid #e5e5e5', borderRadius: '10px' }}>
                            <Typography sx={{ fontSize: '12px', color: '#888', mb: '3px' }}>کل آگهی‌ها</Typography>
                            <Typography sx={{ fontSize: '20px', fontWight: 500 }}>{cities.length > 0 ? cities.reduce((acc, c) => acc + (c._count?.businesses || 0), 0) : '—'}</Typography>
                        </Paper>
                        <Paper elevation={0} sx={{ p: '12px 20px', flex: 1, minWidth: '120px', border: '0.5px solid #e5e5e5', borderRadius: '10px' }}>
                            <Typography sx={{ fontSize: '12px', color: '#888', mb: '3px' }}>شهرهای فعال</Typography>
                            <Typography sx={{ fontSize: '20px', fontWight: 500 }}>{cities.length > 0 ? cities.filter(c => (c._count?.businesses || 0) > 0).length : '—'}</Typography>
                        </Paper>
                        <Paper elevation={0} sx={{ p: '12px 20px', flex: 1, minWidth: '120px', border: '0.5px solid #e5e5e5', borderRadius: '10px' }}>
                            <Typography sx={{ fontSize: '12px', color: '#888', mb: '3px' }}>برترین شهر</Typography>
                            <Typography sx={{ fontSize: '20px', fontWight: 500 }}>
                                {cities.length > 0 ? [...cities].sort((a, b) => (b._count?.businesses || 0) - (a._count?.businesses || 0))[0].name : '-'}
                            </Typography>
                        </Paper>
                    </Box>

                    <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#999', letterSpacing: '.07em', textTransform: 'uppercase', mb: '10px', textAlign: 'right' }}>
                        همه شهرها
                    </Typography>

                    <Grid container spacing={1.5} sx={{ direction: 'rtl' }}>
                        {(() => {
                            const maxCount = Math.max(...cities.map(c => c._count?.businesses || 0), 1);
                            const sortedCities = [...cities].filter(c => c.name !== 'Iran').sort((a, b) => (b._count?.businesses || 0) - (a._count?.businesses || 0));

                            const cityIcons: Record<string, React.ReactNode> = {
                                Sydney: <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><rect width="44" height="44" rx="10" fill="#E6F1FB" /><rect x="4" y="32" width="36" height="4" rx="1" fill="#85B7EB" /><path d="M10 32 Q14 18 18 32" fill="#378ADD" /><path d="M18 32 Q22 12 26 32" fill="#185FA5" /><path d="M26 32 Q30 18 34 32" fill="#378ADD" /><rect x="19" y="24" width="6" height="8" rx="1" fill="#0C447C" /><rect x="7" y="28" width="4" height="4" rx="1" fill="#B5D4F4" /><rect x="27" y="28" width="4" height="4" rx="1" fill="#B5D4F4" /><circle cx="22" cy="10" r="3" fill="#FAC775" /></svg>,
                                Melbourne: <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><rect width="44" height="44" rx="10" fill="#EAF3DE" /><rect x="4" y="34" width="36" height="3" rx="1" fill="#97C459" /><rect x="8" y="20" width="10" height="14" rx="1" fill="#3B6D11" /><rect x="10" y="22" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="14" y="22" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="10" y="27" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="14" y="27" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="20" y="14" width="8" height="20" rx="1" fill="#27500A" /><rect x="22" y="16" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="26" y="16" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="22" y="22" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="26" y="22" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="22" y="28" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="26" y="28" width="2" height="3" rx="0.5" fill="#EAF3DE" /><rect x="30" y="24" width="8" height="10" rx="1" fill="#639922" /><rect x="32" y="26" width="2" height="2" rx="0.5" fill="#EAF3DE" /><rect x="34" y="26" width="2" height="2" rx="0.5" fill="#EAF3DE" /><rect x="32" y="30" width="2" height="2" rx="0.5" fill="#EAF3DE" /><circle cx="33" cy="12" r="2" fill="#FAC775" /></svg>,
                                Adelaide: <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><rect width="44" height="44" rx="10" fill="#FAEEDA" /><rect x="4" y="34" width="36" height="3" rx="1" fill="#EF9F27" /><rect x="14" y="18" width="16" height="16" rx="1" fill="#BA7517" /><polygon points="14,18 22,8 30,18" fill="#854F0B" /><rect x="20" y="26" width="4" height="8" rx="1" fill="#FAEEDA" /><rect x="10" y="24" width="6" height="10" rx="1" fill="#EF9F27" /><rect x="28" y="24" width="6" height="10" rx="1" fill="#EF9F27" /><rect x="11" y="26" width="2" height="2" rx="0.5" fill="#FAEEDA" /><rect x="29" y="26" width="2" height="2" rx="0.5" fill="#FAEEDA" /><circle cx="33" cy="10" r="3" fill="#FAC775" /></svg>,
                                Brisbane: <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><rect width="44" height="44" rx="10" fill="#FAECE7" /><path d="M4 30 Q22 22 40 30" fill="none" stroke="#F0997B" stroke-width="2.5" stroke-linecap="round" /><path d="M8 30 L8 34 L36 34 L36 30" fill="#D85A30" /><rect x="6" y="28" width="4" height="6" rx="1" fill="#993C1D" /><rect x="34" y="28" width="4" height="6" rx="1" fill="#993C1D" /><rect x="12" y="34" width="4" height="2" rx="0.5" fill="#712B13" /><rect x="28" y="34" width="4" height="2" rx="0.5" fill="#712B13" /><rect x="18" y="22" width="8" height="12" rx="1" fill="#993C1D" /><rect x="20" y="24" width="2" height="3" rx="0.5" fill="#FAECE7" /><rect x="24" y="24" width="2" height="3" rx="0.5" fill="#FAECE7" /><rect x="20" y="29" width="4" height="5" rx="0.5" fill="#FAECE7" /><circle cx="33" cy="10" r="3" fill="#FAC775" /></svg>,
                                "Gold Coast": <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><rect width="44" height="44" rx="10" fill="#E1F5EE" /><rect x="4" y="30" width="36" height="6" rx="2" fill="#5DCAA5" /><path d="M4 30 Q12 24 20 30 Q28 24 36 30 Q40 27 44 30" fill="none" stroke="#9FE1CB" stroke-width="2" stroke-linecap="round" /><rect x="8" y="16" width="5" height="14" rx="1" fill="#0F6E56" /><rect x="15" y="12" width="5" height="18" rx="1" fill="#1D9E75" /><rect x="22" y="10" width="5" height="20" rx="1" fill="#0F6E56" /><rect x="29" y="14" width="5" height="16" rx="1" fill="#1D9E75" /><rect x="9" y="18" width="1.5" height="2" rx="0.3" fill="#E1F5EE" /><rect x="11.5" y="18" width="1.5" height="2" rx="0.3" fill="#E1F5EE" /><circle cx="34" cy="8" r="3" fill="#FAC775" /></svg>,
                                Perth: <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><rect width="44" height="44" rx="10" fill="#FBEAF0" /><rect x="4" y="34" width="36" height="3" rx="1" fill="#ED93B1" /><rect x="6" y="24" width="8" height="10" rx="1" fill="#D4537E" /><rect x="8" y="26" width="2" height="2" rx="0.5" fill="#FBEAF0" /><rect x="10" y="26" width="2" height="2" rx="0.5" fill="#FBEAF0" /><rect x="8" y="30" width="2" height="4" rx="0.5" fill="#FBEAF0" /><rect x="16" y="18" width="8" height="16" rx="1" fill="#993556" /><rect x="18" y="20" width="2" height="3" rx="0.5" fill="#FBEAF0" /><rect x="22" y="20" width="2" height="3" rx="0.5" fill="#FBEAF0" /><rect x="18" y="25" width="2" height="3" rx="0.5" fill="#FBEAF0" /><rect x="22" y="25" width="2" height="3" rx="0.5" fill="#FBEAF0" /><rect x="18" y="30" width="4" height="4" rx="0.5" fill="#FBEAF0" /><rect x="26" y="22" width="12" height="12" rx="1" fill="#D4537E" /><rect x="28" y="24" width="2" height="2" rx="0.5" fill="#FBEAF0" /><rect x="32" y="24" width="2" height="2" rx="0.5" fill="#FBEAF0" /><rect x="28" y="28" width="2" height="2" rx="0.5" fill="#FBEAF0" /><rect x="32" y="28" width="2" height="2" rx="0.5" fill="#FBEAF0" /><circle cx="10" cy="10" r="4" fill="#FAC775" /></svg>,
                                Canberra: <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><rect width="44" height="44" rx="10" fill="#EEEDFE" /><rect x="4" y="34" width="36" height="3" rx="1" fill="#AFA9EC" /><polygon points="22,8 28,22 16,22" fill="#534AB7" /><rect x="20" y="22" width="4" height="12" rx="0.5" fill="#3C3489" /><rect x="10" y="26" width="12" height="8" rx="1" fill="#7F77DD" /><rect x="22" y="26" width="12" height="8" rx="1" fill="#7F77DD" /><rect x="12" y="28" width="2" height="2" rx="0.5" fill="#EEEDFE" /><rect x="16" y="28" width="2" height="2" rx="0.5" fill="#EEEDFE" /><rect x="24" y="28" width="2" height="2" rx="0.5" fill="#EEEDFE" /><rect x="28" y="28" width="2" height="2" rx="0.5" fill="#EEEDFE" /><rect x="12" y="32" width="2" height="2" rx="0.5" fill="#EEEDFE" /><rect x="16" y="32" width="2" height="2" rx="0.5" fill="#EEEDFE" /><rect x="24" y="32" width="2" height="2" rx="0.5" fill="#EEEDFE" /><rect x="28" y="32" width="2" height="2" rx="0.5" fill="#EEEDFE" /><circle cx="22" cy="6" r="2" fill="#FAC775" /></svg>,
                                Darwin: <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><rect width="44" height="44" rx="10" fill="#EAF3DE" /><rect x="4" y="32" width="36" height="4" rx="1" fill="#5DCAA5" /><rect x="6" y="26" width="6" height="6" rx="1" fill="#639922" /><rect x="14" y="22" width="6" height="10" rx="1" fill="#3B6D11" /><rect x="22" y="24" width="6" height="8" rx="1" fill="#639922" /><rect x="30" y="20" width="8" height="12" rx="1" fill="#27500A" /><path d="M8 26 Q10 18 12 26" fill="#97C459" /><path d="M16 22 Q18 12 20 22" fill="#C0DD97" /><path d="M24 24 Q26 16 28 24" fill="#97C459" /><path d="M32 20 Q35 10 38 20" fill="#C0DD97" /><circle cx="22" cy="8" r="4" fill="#FAC775" /><circle cx="28" cy="6" r="2" fill="#EF9F27" /></svg>,
                                Tasmania: <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><rect width="44" height="44" rx="10" fill="#F1EFE8" /><path d="M8 34 Q12 20 22 16 Q32 20 36 34 Z" fill="#B4B2A9" /><path d="M14 34 Q16 24 22 20 Q28 24 30 34 Z" fill="#888780" /><path d="M18 34 Q20 28 22 26 Q24 28 26 34 Z" fill="#5F5E5A" /><rect x="4" y="34" width="36" height="3" rx="1" fill="#444441" /><path d="M6 28 Q10 26 14 28" fill="none" stroke="#D3D1C7" stroke-width="1.5" stroke-linecap="round" /><path d="M30 28 Q34 26 38 28" fill="none" stroke="#D3D1C7" stroke-width="1.5" stroke-linecap="round" /><circle cx="22" cy="10" r="3" fill="#B5D4F4" /><circle cx="28" cy="8" r="2" fill="#D3D1C7" /></svg>,
                            };

                            return sortedCities.slice(0, visibleCities).map((city, index) => {
                                const count = city._count?.businesses || 0;
                                const pct = Math.round((count / maxCount) * 100);
                                const isTop = index === 0 && count > 0;

                                return (
                                    <Grid key={city.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                        <Paper
                                            component={RouterLink}
                                            to={cityPath(city)}
                                            elevation={0}
                                            sx={{
                                                display: 'block',
                                                textDecoration: 'none !important',
                                                color: 'inherit',
                                                p: '13px 15px',
                                                borderRadius: '12px',
                                                border: isTop ? '0.5px solid #e5e5e5' : '0.5px solid #e5e5e5',
                                                borderTop: isTop ? '2px solid #2563eb' : '0.5px solid #e5e5e5',
                                                borderTopLeftRadius: isTop ? 0 : '12px',
                                                borderTopRightRadius: isTop ? 0 : '12px',
                                                bgcolor: '#fff',
                                                cursor: 'pointer',
                                                transition: 'border-color .15s, box-shadow .15s',
                                                '&:hover': {
                                                    borderColor: '#ccc',
                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                                                },
                                                opacity: count === 0 ? 0.7 : 1
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '10px' }}>
                                                <Box sx={{ width: 44, height: 44, borderRadius: '10px', overflow: 'hidden', flexShrink: 0 }}>
                                                    {cityIcons[city.name] || cityIcons['Sydney']}
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: '3px', color: '#1a1a1a' }}>{city.name}</Typography>
                                                    {isTop && (
                                                        <Box component="span" sx={{ display: 'inline-block', fontSize: '10px', px: '7px', py: '2px', borderRadius: '6px', bgcolor: '#dbeafe', color: '#1d4ed8' }}>
                                                            برترین شهر
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Typography sx={{ fontSize: '26px', fontWeight: 500, mb: '2px', color: count === 0 ? '#ccc' : '#1a1a1a' }}>{count}</Typography>
                                            <Typography sx={{ fontSize: '12px', color: '#888' }}>آگهی فعال</Typography>
                                            <Box sx={{ height: '3px', bgcolor: '#f0f0f0', borderRadius: '2px', mt: '10px' }}>
                                                <Box sx={{ height: '3px', borderRadius: '2px', bgcolor: count === 0 ? '#e5e5e5' : '#2563eb', width: `${pct}%` }} />
                                            </Box>
                                        </Paper>
                                    </Grid>
                                );
                            });
                        })()}
                    </Grid>

                    {cities.filter(c => c.name !== 'Iran').length > visibleCities && (
                        <Box sx={{ textAlign: 'center', mt: 6 }}>
                            <Button
                                variant="outlined"
                                onClick={() => setVisibleCities(prev => prev + 8)}
                                sx={{
                                    borderRadius: 3,
                                    px: 4,
                                    py: 1,
                                    color: '#2854a1',
                                    borderColor: '#2854a1',
                                    fontWeight: 700,
                                    '&:hover': {
                                        borderColor: '#1a3a7a',
                                        bgcolor: 'rgba(40, 84, 161, 0.05)'
                                    }
                                }}
                            >
                                مشاهده بیشتر
                            </Button>
                        </Box>
                    )}
                </Container>
            </Box>

            {/* Why Cheesta (Features) */}
            <Container sx={{ py: { xs: 8, md: 15 } }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ direction: 'rtl' }}>
                            <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mb: 3, color: '#1a202c' }}>
                                چرا چیستا استرالیا؟
                            </Typography>
                            <Stack spacing={3}>
                                {[
                                    { title: 'بروزترین اطلاعات', desc: 'تلاش ما همواره بر این بوده که دقیق‌ترین و بروزترین اطلاعات کسب و کارها را فراهم کنیم.' },
                                    { title: 'دسترسی سریع', desc: 'با سیستم جستجوی هوشمند چیستا، در کمترین زمان لیست مورد نظر خود را بیابید.' },
                                    { title: 'جامعه ایرانیان', desc: 'حمایت از کسب و کارهای ایرانی در استرالیا و تقویت پیوندهای تجاری جامعه.' }
                                ].map((item, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', gap: 2 }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 2,
                                            bgcolor: 'rgba(40, 84, 161, 0.1)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <Typography sx={{ color: '#2854a1', fontWeight: 'bold' }}>{idx + 1}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 0.5 }}>{item.title}</Typography>
                                            <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                            <Button
                                variant="contained"
                                size="large"
                                sx={{ mt: 5, bgcolor: '#2854a1', borderRadius: 2, px: 4 }}
                                onClick={() => navigate('/user/business/register')}
                            >
                                ثبت رایگان کسب و کار شما
                            </Button>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    bgcolor: 'rgba(40, 84, 161, 0.03)',
                                    borderRadius: '50%',
                                    filter: 'blur(60px)',
                                    zIndex: 0
                                }}
                            />
                            <Box
                                component="img"
                                src="/cheesta-illustration.jpg"
                                alt="Cheesta Community"
                                sx={{
                                    width: '100%',
                                    maxWidth: 600,
                                    borderRadius: 0,
                                    position: 'relative',
                                    zIndex: 1,
                                    filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.1))',
                                    transition: 'transform 0.5s ease',
                                    '&:hover': {
                                        transform: 'scale(1.02)'
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* Performance Analytics Feature Section */}
            <Box sx={{ py: { xs: 8, md: 15 }, bgcolor: '#eff6ff' }}>
                <Container maxWidth="lg">
                    <Grid container spacing={8} alignItems="center">
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                sx={{
                                    borderRadius: 5,
                                    overflow: 'hidden',
                                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
                                    bgcolor: 'white',
                                    p: 3,
                                    position: 'relative'
                                }}
                            >
                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={800} color="#1e293b">آمار بازدید آگهی (نمونه)</Typography>
                                        <Typography variant="caption" color="text.secondary">فعالیت ۷ روز گذشته</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="h5" fontWeight={800} color="#2854a1">1,284</Typography>
                                        <Typography variant="caption" color="success.main" fontWeight={700} dir="rtl">+12.5% نسبت به هفته قبل</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ height: 200, width: '100%', mb: 3 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={[
                                            { date: 'Sat', views: 45 },
                                            { date: 'Sun', views: 52 },
                                            { date: 'Mon', views: 38 },
                                            { date: 'Tue', views: 65 },
                                            { date: 'Wed', views: 48 },
                                            { date: 'Thu', views: 72 },
                                            { date: 'Fri', views: 85 },
                                        ]}>
                                            <defs>
                                                <linearGradient id="colorViewsHome" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#2854a1" stopOpacity={0.3} />
                                                    <stop offset="95%" stopColor="#2854a1" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="date"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fontSize: 10, fill: '#64748b' }}
                                            />
                                            <YAxis hide />
                                            <RechartsTooltip
                                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="views"
                                                stroke="#2854a1"
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill="url(#colorViewsHome)"
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </Box>

                                <Divider sx={{ mb: 3 }} />

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                            <CircularProgress
                                                variant="determinate"
                                                value={85}
                                                size={48}
                                                thickness={4}
                                                sx={{ color: '#10b981' }}
                                            />
                                            <Box
                                                sx={{
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    right: 0,
                                                    position: 'absolute',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography variant="caption" fontWeight={800}>85%</Typography>
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" fontWeight={800} sx={{ display: 'block' }}>امتیاز آگهی</Typography>
                                            <Typography variant="caption" color="text.secondary">بهینه‌سازی عالی</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Box sx={{ p: 0.5, bgcolor: '#f1f5f9', borderRadius: 1, fontSize: '12px' }}>⭐</Box>
                                        <Box sx={{ p: 0.5, bgcolor: '#f1f5f9', borderRadius: 1, fontSize: '12px' }}>💡</Box>
                                    </Box>
                                </Box>

                                {/* Floating Stat Card Overlay */}
                                <Paper
                                    elevation={10}
                                    sx={{
                                        position: 'absolute',
                                        top: 80,
                                        left: -20,
                                        p: 1.5,
                                        borderRadius: 3,
                                        display: { xs: 'none', lg: 'block' },
                                        border: '1px solid #e2e8f0',
                                        zIndex: 2
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ p: 0.8, bgcolor: '#dcfce7', borderRadius: 2, color: '#166534', fontSize: '14px' }}>
                                            🚀
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>اوج روزانه</Typography>
                                            <Typography variant="body2" fontWeight={800}>+42%</Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ direction: 'rtl' }}>
                                <Typography
                                    variant="h3"
                                    component="h2"
                                    sx={{
                                        fontWeight: 800,
                                        mb: 3,
                                        color: '#1a202c'
                                    }}
                                >
                                    تحلیل حرفه‌ای عملکرد آگهی
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#64748b', mt: 3, mb: 4, lineHeight: 1.8, fontSize: '1.1rem' }}>
                                    ما در چیستا ابزارهای پیشرفته‌ای را در اختیار شما قرار می‌دهیم تا نبض کسب‌ و کار خود را در دست بگیرید.
                                    به عنوان صاحب آگهی، شما به پنل اختصاصی تحلیل آمار دسترسی خواهید داشت تا بازدهی آگهی خود را به حداکثر برسانید.
                                </Typography>
                                <Stack spacing={3} sx={{ mb: 5 }}>
                                    {[
                                        { title: 'نمودار بازدیدهای روزانه و هفتگی', icon: '📊', color: '#3b82f6' },
                                        { title: 'امتیازدهی هوشمند به کیفیت محتوا', icon: '✨', color: '#f59e0b' },
                                        { title: 'پیشنهادات اختصاصی برای سئو و جذب مشتری', icon: '🚀', color: '#10b981' },
                                        { title: 'گزارش کامل از تعاملات کاربران', icon: '👥', color: '#6366f1' }
                                    ].map((item, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                            <Box sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: '12px',
                                                bgcolor: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                                                fontSize: '1.4rem'
                                            }}>
                                                {item.icon}
                                            </Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155' }}>
                                                {item.title}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        bgcolor: '#2854a1',
                                        borderRadius: 3,
                                        px: 5,
                                        py: 1.5,
                                        fontWeight: 800,
                                        fontSize: '1rem',
                                        boxShadow: '0 10px 15px -3px rgba(40, 84, 161, 0.3)',
                                        '&:hover': {
                                            bgcolor: '#1e3d7a',
                                            transform: 'translateY(-2px)'
                                        },
                                        transition: 'all 0.2s'
                                    }}
                                    onClick={() => navigate('/user')}
                                >
                                    مشاهده پنل آمار من
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Website Auto-Scan Feature Section */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                <Grid container spacing={8} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ direction: 'rtl' }}>
                            <Box sx={{ display: 'inline-block', px: 1.5, py: 0.4, mb: 2, bgcolor: '#ecfdf5', color: '#047857', borderRadius: 2, fontSize: '0.8rem', fontWeight: 800, border: '1px solid #a7f3d0' }}>
                                جدید
                            </Box>
                            <Typography variant="h3" component="h2" sx={{ fontWeight: 800, mb: 3, color: '#1a202c' }}>
                                ثبت سریع با اسکن خودکار وب‌سایت
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#64748b', mb: 4, lineHeight: 1.8, fontSize: '1.1rem' }}>
                                اگر کسب‌وکار شما وب‌سایت دارد، لازم نیست همه اطلاعات را دستی وارد کنید. آدرس وب‌سایت را بدهید، مالکیت آن را با یک کد ایمیلی تأیید کنید و بگذارید چیستا پیش‌نویس آگهی شما را بسازد.
                            </Typography>
                            <Stack spacing={2.5} sx={{ mb: 5 }}>
                                {[
                                    { title: 'تأیید مالکیت وب‌سایت با کد ایمیلی', icon: '🔐' },
                                    { title: 'ساخت خودکار توضیحات فارسی و انگلیسی، خدمات و تصاویر', icon: '✨' },
                                    { title: 'بررسی و ویرایش پیش‌نویس قبل از ثبت نهایی', icon: '📝' }
                                ].map((item, idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08)', fontSize: '1.4rem' }}>
                                            {item.icon}
                                        </Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155' }}>
                                            {item.title}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        bgcolor: '#2854a1', borderRadius: 3, px: 5, py: 1.5, fontWeight: 800, fontSize: '1rem',
                                        boxShadow: '0 10px 15px -3px rgba(40, 84, 161, 0.3)',
                                        '&:hover': { bgcolor: '#1e3d7a', transform: 'translateY(-2px)' },
                                        transition: 'all 0.2s'
                                    }}
                                    onClick={() => navigate('/user/website-scan')}
                                >
                                    اسکن خودکار وب‌سایت من
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="large"
                                    sx={{ borderRadius: 3, px: 4, py: 1.5, fontWeight: 700, borderColor: '#2854a1', color: '#2854a1' }}
                                    onClick={() => navigate('/cheesta-help#autoscan')}
                                >
                                    راهنمای مرحله‌به‌مرحله
                                </Button>
                            </Stack>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper
                            elevation={0}
                            onClick={() => navigate('/user/website-scan')}
                            role="link"
                            sx={{
                                cursor: 'pointer', p: { xs: 3, md: 4 }, borderRadius: 5, direction: 'rtl',
                                background: 'linear-gradient(135deg, rgba(40, 84, 161, 0.06) 0%, rgba(40, 84, 161, 0.14) 100%)',
                                border: '1px solid rgba(40, 84, 161, 0.18)',
                                transition: 'all 0.25s',
                                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 25px 50px -12px rgba(40,84,161,0.25)' }
                            }}
                        >
                            <Stack spacing={2.5}>
                                {[
                                    { n: 1, title: 'آدرس وب‌سایت و ایمیل', desc: 'مثلاً info@yourbusiness.com.au' },
                                    { n: 2, title: 'کد ۶ رقمی ایمیل', desc: 'تا ۳۰ دقیقه معتبر است' },
                                    { n: 3, title: 'اسکن و ساخت پیش‌نویس', desc: 'نتیجه در «وب‌سایت‌های اسکن‌شده»' }
                                ].map(step => (
                                    <Box key={step.n} sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'white', p: 2, borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.06)' }}>
                                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#2854a1', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 }}>
                                            {step.n}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 800, color: '#1e293b' }}>{step.title}</Typography>
                                            <Typography variant="body2" color="text.secondary">{step.desc}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            {/* Business Success Quote Section */}
            <Box
                sx={{
                    position: 'relative',
                    height: { xs: '180px', md: '260px' }, // Further reduced height
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                }}
            >
                <Box
                    component="img"
                    src="/Cheesta-marketing.jpg"
                    alt="Business Success"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover', // Stretched to cover sides
                        zIndex: 0,
                        filter: 'brightness(0.6)' // Using brightness again for full bleed contrast
                    }}
                />
                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            color: 'white',
                            fontWeight: 800,
                            lineHeight: 1.8,
                            direction: 'rtl',
                            fontSize: { xs: '1.1rem', md: '1.2rem' },
                            textShadow: '0 4px 15px rgba(0,0,0,0.8)', // Stronger shadow for readability without box
                            maxWidth: '800px',
                            mx: 'auto'
                        }}
                    >
                        موفقیت هر کسب و کاری در گرو برآورده ساختن نیاز مشتری و بازاریابی آن نهفته است

                    </Typography>
                </Container>
            </Box>
        </Box>
    );
};

export default HomePage;
